// Disabling this because it breaks SSR! @TODO find root of problem
// import TextareaAutosize from 'react-textarea-autosize';
import { css } from '@emotion/react';
import React, { CSSProperties } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { cn } from '@/common/helpers';
import { useTheme } from '@/ui/theme';

import { FieldErrorMessage, Label } from '.';

interface TextAreaProps {
  label?: string;
  style?: CSSProperties;
  withBottomMargin?: boolean;
  isRequired?: boolean;
  hideError?: boolean;
  error?: string;
  value?: string;
}

export const TextArea = React.forwardRef(
  (
    {
      label,
      name,
      style,
      withBottomMargin,
      isRequired,
      hideError,
      error,
      value,
      ...rest
    }: TextAreaProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    ref: React.ForwardedRef<HTMLTextAreaElement>
  ) => {
    const fieldId = `field-${name}`;
    const {
      colors,
      fonts,
      keylines,
      text,
      forms: { input },
    } = useTheme();

    const containerStyles = css`
      display: flex;
      flex-direction: column;
      flex: 1;

      margin-bottom: ${withBottomMargin ? '30px' : '0'};

      &.error {
        label {
          color: ${colors.error};
        }
        textarea {
          border-bottom: 1px solid ${colors.error};
          /* color: red; */
        }
        .error-message {
          visibility: visible;
        }

        &.empty {
          ::placeholder {
            color: ${colors.error};
          }
        }
      }

      textarea {
        border: none;
        padding: 16px;
        outline: none;
        display: block;
        background: ${input?.backgroundColor ?? 'transparent'};
        color: ${input?.color ?? colors.textPrimary};

        font-family: ${fonts.body};
        font-size: ${input?.fontSize ?? text.bodyCopy.fontSize};
        border: ${input?.border ?? keylines?.border};
        border-radius: ${input?.borderRadius ?? '4px'};

        &.focus-visible {
          outline: 1px solid ${colors.textSecondary};
          outline-offset: 2px;
        }

        ::placeholder {
          color: ${input?.placeholder?.color ?? '#718096'};
        }
      }
    `;

    const showError = !!error;
    const isEmpty = value && value.length <= 0;

    return (
      <div
        css={containerStyles}
        style={style}
        className={cn(showError && 'error', isEmpty && 'empty')}
      >
        {label && (
          <Label htmlFor={fieldId} isRequired={isRequired}>
            {label}
          </Label>
        )}
        <TextareaAutosize
          ref={ref as any}
          name={name}
          id={fieldId}
          aria-describedby={`${fieldId}-error`}
          aria-required={isRequired}
          minRows={4}
          {...rest}
        />
        {hideError ? null : (
          <FieldErrorMessage
            fieldName={name}
            message={error}
            isVisible={showError}
          />
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
